/**
 * Projects in the browser: IndexedDB, metadata only.
 *
 * A record is the serialised project (a few kilobytes: what goes where, and a
 * description of each file) plus, where the browser gave us them, file handles
 * that let the files be reopened with the user's permission. No media bytes,
 * ever. IndexedDB rather than localStorage because a handle can be structured
 * cloned but not stringified.
 */
import { parseProject, projectDuration, serializeProject, type Project } from "./timeline.ts";

const DB_NAME = "otterstudio";
const STORE = "projects";
const VERSION = 1;

export interface ProjectSummary {
  id: string;
  name: string;
  updatedAt: number;
  duration: number;
  clipCount: number;
  sourceCount: number;
  /** A small JPEG of the first video's first frame, from the source record. */
  poster?: string;
}

interface StoredProject extends ProjectSummary {
  record: string;
  handles: Record<string, FileSystemFileHandle>;
}

export const storageAvailable = (): boolean => typeof indexedDB !== "undefined";

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!storageAvailable()) {
      reject(new Error("This browser has no IndexedDB, so projects cannot be kept between visits."));
      return;
    }
    const request = indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" }).createIndex("updatedAt", "updatedAt");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open project storage"));
    request.onblocked = () => reject(new Error("Project storage is open in another tab and blocked"));
  });
}

function run<T>(db: IDBDatabase, mode: IDBTransactionMode, act: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const request = act(transaction.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Project storage failed"));
    transaction.oncomplete = () => db.close();
    transaction.onabort = () => {
      db.close();
      reject(transaction.error ?? new Error("Project storage aborted"));
    };
  });
}

const summarise = (stored: StoredProject): ProjectSummary => ({
  id: stored.id,
  name: stored.name,
  updatedAt: stored.updatedAt,
  duration: stored.duration,
  clipCount: stored.clipCount,
  sourceCount: stored.sourceCount,
  ...(stored.poster ? { poster: stored.poster } : {}),
});

/** Newest first. */
export async function listProjects(): Promise<ProjectSummary[]> {
  const db = await open();
  const all = await run(db, "readonly", (store) => store.getAll() as IDBRequest<StoredProject[]>);
  return all.map(summarise).sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function loadProject(
  id: string,
): Promise<{ project: Project; handles: Map<string, FileSystemFileHandle> } | null> {
  const db = await open();
  const stored = await run(db, "readonly", (store) => store.get(id) as IDBRequest<StoredProject | undefined>);
  if (!stored) return null;
  return { project: parseProject(stored.record), handles: new Map(Object.entries(stored.handles ?? {})) };
}

/**
 * Write the project. Handles are kept for the sources that have them; if the
 * browser cannot clone them the record is written without, which only costs
 * the "open the files" shortcut.
 */
export async function saveProject(project: Project, handles: Map<string, FileSystemFileHandle>): Promise<void> {
  const poster = project.sources.find((source) => source.poster)?.poster;
  const base: StoredProject = {
    id: project.id,
    name: project.name,
    updatedAt: project.updatedAt,
    duration: projectDuration(project),
    clipCount: project.clips.length,
    sourceCount: project.sources.filter((source) => !source.derived).length,
    ...(poster ? { poster } : {}),
    record: serializeProject(project),
    handles: Object.fromEntries(
      [...handles].filter(([sourceId]) => project.sources.some((source) => source.id === sourceId)),
    ),
  };
  const db = await open();
  try {
    await run(db, "readwrite", (store) => store.put(base));
  } catch (error) {
    if ((error as DOMException)?.name !== "DataCloneError") throw error;
    const again = await open();
    await run(again, "readwrite", (store) => store.put({ ...base, handles: {} }));
  }
}

export async function deleteProject(id: string): Promise<void> {
  const db = await open();
  await run(db, "readwrite", (store) => store.delete(id));
}
